import React, { useEffect } from 'react';
import styled from 'styled-components';

import {
  TagField,
  TextField,
  CheckboxField,
  DateField,
  TextareaField,
  DropzoneField,
  PhoneField,
  SelectField,
} from '../formik/fields';

const InfoRequestForm = props => {
  const { formik, matters, groups: parentGroups, setRequireds, form } = props;
  const { values, setFieldValue } = formik;

  // Conditional field logic! Matter is chosen first, then group
  // Matter
  const requestMatter = values?.requestMatter;
  const matter =
    requestMatter && matters && matters.find(m => m.value === requestMatter);

  let extrafields = matter?.extrafields;

  // These groups come from the matter
  // They don't have extrafields of their own like parentGroups
  // const groups = extrafields?.find(f => f.field === 'serviceGroup')?.options;

  const serviceLineOptions = extrafields
    ?.find(f => f.field === 'serviceLine')
    ?.options?.map(o => ({ value: o.id || o.value, label: o.text }));

  const serviceGroup = values?.serviceGroup;
  const group =
    serviceGroup && parentGroups?.find(g => g.value === serviceGroup.value);

  // Combine extra fields
  extrafields =
    (extrafields && group && [...extrafields, ...(group?.extrafields || [])]) ||
    extrafields;
  const extrastrings = extrafields
    ? Object.values(extrafields).map(f => f.field)
    : [];

  // const resetExtraFields = () => {
  //   extrastrings.forEach(s => {
  //     setFieldValue(s, initialValues[s]);
  //     setFieldTouched(s, false);
  //   });
  // };

  const getRequireds = (matter, group) => {
    const requireds = {
      requestMatter: true,
      serviceLine: false,
      serviceGroup: matter !== 'MATTER_OTHER',
      topic: true,
      requestDescription: true,
      researchCode:
        matter === 'MATTER_RESEARCH' && !['GROUP_M110'].includes(group),
      researchProjectNumber:
        matter === 'MATTER_RESEARCH' && !['GROUP_M110'].includes(group),
      classification: matter === 'MATTER_BIOBANK',
      patientInfoRequested: false,
      permitAttachment: !!values.patientInfoRequested,
      requestHasDeadline: false,
      deadline: !!values.requestHasDeadline,
      deadlineArgument: !!values.requestHasDeadline,
      customerIsNotNotifier: false,
      customerEmail: !!values.customerIsNotNotifier,
      requestAttachment:
        matter === 'MATTER_RESEARCH' &&
        !['GROUP_M708', 'GROUP_M110'].includes(group),
      requestAttachment2:
        matter === 'MATTER_RESEARCH' &&
        !['GROUP_M708', 'GROUP_M110', 'GROUP_FINDATA_M116'].includes(group),
      requestAttachment3: false,
      costcenter:
        matter === 'MATTER_RESEARCH' &&
        ![
          'GROUP_M110',
          'GROUP_FINDATA_M116', // Not visible
          'GROUP_OTHER_OUTER_M116', // Not visible
          'GROUP_OWN_COST_M117', // Not visible
        ].includes(group),
      billingAddress:
        matter === 'MATTER_RESEARCH' && group === 'GROUP_OWN_COST_M117',
      contactNumber: false,
      requestForContact: false,
      requestIsReviewed:
        (matter === 'MATTER_OPERATION' && group !== 'GROUP_RAFAELA_UPDATE') ||
        matter === 'MATTER_RESEARCH',
    };

    return requireds;
  };

  // TODO This could probably be refactored out into the onChanges
  useEffect(() => {
    setRequireds(getRequireds(matter?.value, group?.value));
  }, [matter, group]);

  // We can't use the one from the parent component because it will be stale
  const requireds = getRequireds(matter?.value, group?.value);
  const isRequired = value => requireds[value];

  // Augment each field with these props
  // The props are merged from the form definition AND the extrafields gathered
  // from the chosen matter and the chosen group
  const getExtrafieldProps = name => {
    let props = {};

    const formMatch = form?.fields.find(f => f.name === name);
    const extraMatch = extrafields?.find(f => f.field === name);

    if (formMatch) {
      const { info, label, options, placeholder } = formMatch;
      props = { info, label, options, placeholder };
    }

    // Don't accidentally override potentially useful values here
    if (extraMatch) {
      const { info, label, options, placeholder } = extraMatch;
      if (info) props.info = info;
      if (label) props.label = label;
      if (options)
        props.options = options.map(o => ({ value: o.value, label: o.text }));
      if (placeholder) props.placeholder = placeholder;
    }

    props.required = isRequired(name);

    return props;
  };

  const showDisclaimer =
    matter &&
    ['MATTER_ECONOMY', 'MATTER_OPERATION', 'MATTER_OTHER'].includes(
      matter.value
    );

  return (
    <Container>
      <TagField
        {...getExtrafieldProps('requestMatter')}
        name="requestMatter"
        // Clear service group whenever matter is chosen, because the
        // group options will be different
        onChange={value => {
          if (value === values.requestMatter) {
            return;
          }

          setFieldValue('serviceGroup', null);
          setFieldValue('serviceLine', null);
          // resetExtraFields();
        }}
      />
      {values.requestMatter && (
        <>
          {extrastrings.includes('serviceLine') && (
            <SelectField
              {...getExtrafieldProps('serviceLine')}
              name="serviceLine"
              isMulti
              options={serviceLineOptions}
              isClearable
            />
          )}

          {extrastrings.includes('serviceGroup') && (
            <SelectField
              {...getExtrafieldProps('serviceGroup')}
              name="serviceGroup"
              onChange={value => {
                if (!value || value === values.serviceGroup) {
                  return;
                }

                // For this specific group, we want patient info requested
                // to be pre-ticked
                if (value.value === 'GROUP_PATIENT_INFO_PICKUP') {
                  setFieldValue('patientInfoRequested', true);
                }
              }}
            />
          )}

          <TextField {...getExtrafieldProps('topic')} name="topic" />
          <TextareaField
            {...getExtrafieldProps('requestDescription')}
            name="requestDescription"
          />

          {extrastrings.includes('researchCode') && (
            <TextField
              {...getExtrafieldProps('researchCode')}
              name="researchCode"
            />
          )}

          {extrastrings.includes('researchProjectNumber') && (
            <TextField
              {...getExtrafieldProps('researchProjectNumber')}
              name="researchProjectNumber"
            />
          )}

          {extrastrings.includes('classification') && (
            <TagField
              {...getExtrafieldProps('classification')}
              name="classification"
            />
          )}

          {extrastrings.includes('patientInfoRequested') && (
            <CheckboxField
              {...getExtrafieldProps('patientInfoRequested')}
              name="patientInfoRequested"
            >
              {values.patientInfoRequested && (
                <>
                  <Disclaimer>
                    Potilaskohtaisen tiedon (esim. hetujen) pyyntö edellyttää
                    vastuualuejohtajan/viranhaltijan lupaa henkilötietojen
                    luovuttamista ja käsittelyä varten. Mikäli sinulla ei ole
                    vielä saatua lupavahvistusta, hanki se ensin, ja palaa
                    sitten täyttämään lomake. Lupavahvistukseksi riittää esim.
                    ote tai kuvankaappaus vastuualuejohtajan/viranhaltijan
                    sähköpostiviestistä, jossa on hänen henkilökohtainen
                    kuittaus potilaskohtaisen poiminnan hyväksynnästä.
                  </Disclaimer>
                  <DropzoneField
                    {...getExtrafieldProps('permitAttachment')}
                    name="permitAttachment"
                    required
                  />
                </>
              )}
            </CheckboxField>
          )}

          <CheckboxField
            {...getExtrafieldProps('requestHasDeadline')}
            name="requestHasDeadline"
          >
            {values.requestHasDeadline && (
              <Deadline>
                <DateField
                  {...getExtrafieldProps('deadline')}
                  name="deadline"
                  required
                />
                <TextareaField
                  {...getExtrafieldProps('deadlineArgument')}
                  name="deadlineArgument"
                  required
                />
              </Deadline>
            )}
          </CheckboxField>

          {extrastrings.includes('customerIsNotNotifier') && (
            <CheckboxField
              {...getExtrafieldProps('customerIsNotNotifier')}
              name="customerIsNotNotifier"
            >
              {values.customerIsNotNotifier &&
                extrastrings.includes('customerEmail') && (
                  <TextField
                    {...getExtrafieldProps('customerEmail')}
                    name="customerEmail"
                    required
                  />
                )}
            </CheckboxField>
          )}

          <DropzoneField
            {...getExtrafieldProps('requestAttachment')}
            name="requestAttachment"
          />

          {extrastrings.includes('requestAttachment2') && (
            <DropzoneField
              {...getExtrafieldProps('requestAttachment2')}
              name="requestAttachment2"
            />
          )}

          {extrastrings.includes('requestAttachment3') && (
            <DropzoneField
              {...getExtrafieldProps('requestAttachment3')}
              name="requestAttachment3"
            />
          )}

          {extrastrings.includes('costcenter') && (
            <TextField
              {...getExtrafieldProps('costcenter')}
              name="costcenter"
            />
          )}

          {extrastrings.includes('billingAddress') && (
            <TextField
              {...getExtrafieldProps('billingAddress')}
              name="billingAddress"
            />
          )}

          <PhoneField
            {...getExtrafieldProps('contactNumber')}
            name="contactNumber"
          />
          <CheckboxField
            {...getExtrafieldProps('requestForContact')}
            name="requestForContact"
          />

          {showDisclaimer && (
            <Disclaimer>
              Mikäli pyyntösi toteuttamiseen kuluvan työmäärän arvio on yli 1300
              € (2 htp tai enemmän), niin olemme sinuun erikseen yhteydessä
              käytännön asioiden osalta.
            </Disclaimer>
          )}

          {extrastrings.includes('requestIsReviewed') && (
            <CheckboxField
              {...getExtrafieldProps('requestIsReviewed')}
              name="requestIsReviewed"
              required
            />
          )}
        </>
      )}
    </Container>
  );
};

const Container = styled.div`
  & > * {
    margin-bottom: 24px;
  }
`;

const Deadline = styled.div`
  & > * {
    margin-bottom: 24px;
  }
`;

const Disclaimer = styled.p`
  font-size: 16px;
`;

export default InfoRequestForm;
